import type { FaqItem } from "./types";
import type { Wave1SeoLocale } from "./locale-rollout";
import { chineseHomeContent } from "./chinese-home";
import { koreanHomeContent } from "./korean-home";
import { supplementalHomeContent } from "./supplemental-home";

export type HomepageSampleKey =
  | "history"
  | "scary"
  | "longForm"
  | "education"
  | "url";

interface WorkflowCard {
  title: string;
  description: string;
  output: string;
  href: string;
}

interface ProcessStep {
  title: string;
  description: string;
}

interface HomepageSampleCopy {
  label: string;
  title: string;
  description: string;
  transcript: string;
}

export interface Wave1HomeContent {
  metadata: {
    title: string;
    description: string;
    keywords: string[];
  };
  hero: {
    eyebrow: string;
    title: string;
    description: string;
    primary: string;
    secondary: string;
    previewAlt: string;
    caption: string;
  };
  workflowTypes: {
    title: string;
    subtitle: string;
    action: string;
    items: WorkflowCard[];
  };
  samples: {
    title: string;
    description: string;
    cta: string;
    publicExample: string;
    englishDisclosure: string;
    secondShort: string;
    minuteShort: string;
    items: Record<HomepageSampleKey, HomepageSampleCopy>;
  };
  process: {
    title: string;
    description: string;
    steps: ProcessStep[];
    controlsTitle: string;
    controls: string[];
    platformsEyebrow: string;
    platformsTitle: string;
    platformsDescription: string;
    platformsSupportedLabel: string;
    platformsDisclaimer: string;
    directoryTitle: string;
    directoryDescription: string;
    directoryLabel: string;
  };
  pricing: {
    title: string;
    subtitle: string;
    view: string;
    credits: string;
    estimate: string;
    disclaimer: string;
    perYear: string;
    perMonth: string;
    minutes: string;
    planDescriptions: {
      basic: string;
      standard: string;
      pro: string;
    };
  };
  faq: {
    title: string;
    description: string;
    items: FaqItem[];
  };
  cta: {
    title: string;
    description: string;
    button: string;
  };
  navigation: {
    facelessVideo: string;
    useCases: string;
    resources: string;
    pricing: string;
    dashboard: string;
    signIn: string;
    startFree: string;
    menu: string;
    close: string;
    useCaseLinks: Array<{
      href: string;
      label: string;
      description: string;
    }>;
  };
  footer: {
    description: string;
    productTitle: string;
    useCasesTitle: string;
    resourcesTitle: string;
    terms: string;
    privacy: string;
    english: string;
    productLinks: Array<[string, string]>;
    useCaseLinks: Array<[string, string]>;
    resourceLinks: Array<[string, string]>;
  };
  marketing: {
    create: string;
    watchSample: string;
    englishSample: string;
    whoFits: string;
    workflowTitle: string;
    limitsTitle: string;
    relatedTitle: string;
    faqTitle: string;
    finalTitle: string;
    finalDescription: string;
    englishLabel: string;
    viewPublicExample: string;
  };
}

export type HomepagePageContent = Pick<
  Wave1HomeContent,
  | "metadata"
  | "hero"
  | "workflowTypes"
  | "samples"
  | "process"
  | "pricing"
  | "faq"
  | "cta"
>;

const sharedUseCaseHrefs = {
  youtube: "/faceless-youtube-video-generator",
  tiktok: "/faceless-tiktok-video-generator",
  instagram: "/faceless-instagram-reels-generator",
  podcast: "/faceless-podcast-video-generator",
};

export const wave1HomeContent: Record<Wave1SeoLocale, Wave1HomeContent> = {
  pt: {
    metadata: {
      title: "Gerador de Vídeos sem Rosto com IA | AriaFlow",
      description:
        "Crie vídeos sem rosto editáveis a partir de uma ideia, roteiro ou URL, com imagens originais, voz de IA, legendas, revisão e publicação.",
      keywords: [
        "gerador de vídeos sem rosto com IA",
        "gerador de vídeos para canal dark",
        "criar vídeos sem aparecer",
        "automação de vídeos sem rosto",
      ],
    },
    hero: {
      eyebrow: "Gerador de vídeos sem rosto com IA",
      title: "Crie e publique vídeos sem rosto automaticamente",
      description:
        "Comece com uma ideia, roteiro ou URL pública. Revise cada detalhe e publique no seu ritmo, ou automatize o fluxo quando estiver pronto.",
      primary: "Começar grátis",
      secondary: "Ver exemplos reais",
      previewAlt:
        "Fluxo de vídeos sem rosto da AriaFlow com plano de conteúdo editável e status de geração",
      caption:
        "Revise antes de publicar ou automatize o fluxo quando ele estiver pronto.",
    },
    workflowTypes: {
      title: "Crie vídeos sem rosto do seu jeito.",
      subtitle:
        "Comece com um tema, um roteiro pronto ou uma URL pública. Revise o rascunho antes de gerar ou publicar.",
      action: "Explorar este fluxo",
      items: [
        {
          title: "Tema para vídeo sem rosto",
          description:
            "Crie um plano de conteúdo e o primeiro roteiro a partir de uma ideia específica.",
          output: "Ideal para séries novas e temas recorrentes",
          href: "/faceless-video-automation",
        },
        {
          title: "Roteiro para vídeo sem rosto",
          description:
            "Mantenha a narração aprovada e transforme o texto em cenas editáveis.",
          output: "Ideal para roteiristas e conteúdo revisado",
          href: "/script-to-faceless-video",
        },
        {
          title: "URL para vídeo sem rosto",
          description:
            "Adapte uma fonte pública para um rascunho narrado e visual.",
          output: "Ideal para artigos e vídeos explicativos",
          href: "/url-to-faceless-video",
        },
      ],
    },
    samples: {
      title: "Vídeos verticais reais criados com a AriaFlow",
      description:
        "Histórias, diálogos, resenhas e episódios longos: cada exemplo vem de um fluxo real da AriaFlow. Compare ritmo, legendas e direção visual antes de criar o seu.",
      cta: "Criar um vídeo sem rosto",
      publicExample: "Ver exemplo público",
      englishDisclosure: "Exemplo com áudio ou legendas em inglês",
      secondShort: "s",
      minuteShort: "min",
      items: {
        history: {
          label: "História",
          title: "A cidade falsa que recebeu as bombas",
          description:
            "História vertical sobre locais de distração britânicos na Segunda Guerra Mundial, com personagens recorrentes e narração.",
          transcript:
            "O roteiro explica como cidades iluminadas de mentira desviavam bombardeiros de Bristol.",
        },
        scary: {
          label: "História de terror",
          title: "Uma história de suspense",
          description:
            "Narrativa vertical com abertura direta, tensão crescente, legendas e direção visual sombria.",
          transcript:
            "A história começa com um detalhe inexplicável, aumenta o risco cena a cena e termina após a revelação.",
        },
        longForm: {
          label: "Vídeo longo",
          title: "Rotina matinal para vencer a preguiça",
          description:
            "Podcast de nove minutos para aprender inglês, com duas vozes de IA, legendas legíveis e visual consistente.",
          transcript:
            "O episódio ensina vocabulário de rotina matinal por meio de uma conversa longa.",
        },
        education: {
          label: "URL para vídeo",
          title: "Exemplo URL para vídeo da AriaFlow",
          description:
            "A página pública da AriaFlow transformada num vídeo horizontal de 79 segundos com narração baseada na fonte, legendas e cenas de produto.",
          transcript:
            "O roteiro mostra como criar vídeos editáveis a partir de uma ideia, roteiro ou URL e publicá-los nos canais conectados.",
        },
        url: {
          label: "URL para vídeo",
          title: "Visão geral do EnsembleData",
          description:
            "Site condensado em um vídeo de 70 segundos, com roteiro reescrito, cenas de marca, narração e legendas.",
          transcript:
            "A fonte é transformada em uma explicação dos recursos, redes compatíveis e usos da API.",
        },
      },
    },
    process: {
      title: "Um fluxo completo com revisão antes da publicação",
      description:
        "A AriaFlow separa a renderização da autorização para publicar, mantendo as verificações de qualidade visíveis.",
      steps: [
        {
          title: "Entrada",
          description:
            "Comece com um tema específico, roteiro aprovado ou URL pública.",
        },
        {
          title: "Geração",
          description:
            "Prepare narração, cenas, imagens originais, legendas, música e metadados.",
        },
        {
          title: "Revisão",
          description:
            "Confira roteiro, voz, imagens, ritmo, legendas e configurações do canal.",
        },
        {
          title: "Agendar ou publicar",
          description:
            "Baixe o vídeo aprovado ou envie para um canal conectado no horário escolhido.",
        },
      ],
      controlsTitle: "Mantenha o controle do rascunho",
      controls: [
        "Edite o roteiro e o plano de cenas gerados",
        "Escolha voz, estilo visual, proporção e ritmo",
        "Crie vídeos verticais ou horizontais de até 10 minutos",
        "Revise os metadados e a conta de destino",
      ],
      platformsEyebrow: "Publicação conectada",
      platformsTitle: "Publique onde seu público assiste",
      platformsDescription:
        "Conecte contas compatíveis, revise o destino e os metadados e então agende ou publique o vídeo aprovado.",
      platformsSupportedLabel: "Compatível",
      platformsDisclaimer:
        "A publicação depende da autorização da conta e das regras atuais das APIs das plataformas.",
      directoryTitle: "Fluxos por entrada e plataforma",
      directoryDescription:
        "Escolha a página mais próxima do vídeo desejado. Cada fluxo inclui limites práticos, um exemplo e orientações.",
      directoryLabel: "Fluxos de vídeos sem rosto",
    },
    pricing: {
      title: "Preços para o seu ritmo de produção",
      subtitle:
        "Os créditos cobrem roteiro, imagens, voz e composição. Veja uma faixa estimada antes de escolher.",
      view: "Ver preços e custos por unidade",
      credits: "créditos incluídos",
      estimate: "Estimativa de vídeo final",
      disclaimer:
        "A estimativa usa cerca de 500–750 créditos por minuto final. Modelo, voz, ritmo visual, tentativas e duração alteram o consumo real.",
      perYear: "por ano",
      perMonth: "por mês",
      minutes: "min",
      planDescriptions: {
        basic: "Para testar o primeiro fluxo e criar um conjunto consistente de rascunhos.",
        standard:
          "Para criadores que produzem vários vídeos revisados com frequência.",
        pro: "Para equipes que precisam de mais volume em vários fluxos.",
      },
    },
    faq: {
      title: "Perguntas frequentes",
      description:
        "Entenda entradas, duração, publicação e direitos de conteúdo antes do primeiro vídeo.",
      items: [
        {
          question: "O que é um gerador de vídeos sem rosto com IA?",
          answer:
            "É um fluxo que cria vídeos sem exigir que você apareça na câmera. A AriaFlow combina roteiro, voz de IA, imagens, legendas, música e controles de revisão.",
        },
        {
          question: "Posso usar meu próprio roteiro?",
          answer:
            "Sim. Cole o roteiro, revise duração e cenas e escolha voz, estilo visual, legendas, proporção e etapa de publicação.",
        },
        {
          question: "A AriaFlow transforma uma URL em vídeo?",
          answer:
            "A AriaFlow processa páginas públicas compatíveis e cria uma adaptação narrada editável. Páginas privadas ou bloqueadas podem exigir o texto colado manualmente.",
        },
        {
          question: "Qual é a duração máxima?",
          answer:
            "O fluxo atual aceita vídeos de até 10 minutos. Créditos e tempo de geração variam conforme duração, modelo visual, voz e composição.",
        },
        {
          question: "A AriaFlow publica automaticamente?",
          answer:
            "Você pode conectar contas compatíveis do YouTube, TikTok, Instagram e X e revisar o vídeo e o destino antes de agendar ou publicar.",
        },
        {
          question: "O vídeo gerado é meu?",
          answer:
            "O arquivo final é seu, sujeito aos seus direitos sobre a fonte e aos termos dos modelos, mídias, músicas e vozes usados.",
        },
      ],
    },
    cta: {
      title: "Crie seu primeiro vídeo sem rosto revisável",
      description:
        "Comece com uma ideia, roteiro ou URL e confira o rascunho antes de baixar, agendar ou publicar.",
      button: "Criar um vídeo sem rosto",
    },
    navigation: {
      facelessVideo: "Vídeos sem rosto",
      useCases: "Casos de uso",
      resources: "Recursos",
      pricing: "Preços",
      dashboard: "Painel",
      signIn: "Entrar",
      startFree: "Começar grátis",
      menu: "Menu",
      close: "Fechar",
      useCaseLinks: [
        {
          href: sharedUseCaseHrefs.youtube,
          label: "Vídeos para YouTube",
          description:
            "Crie Shorts, vídeos longos e conteúdo para canal dark.",
        },
        {
          href: sharedUseCaseHrefs.tiktok,
          label: "Vídeos para TikTok",
          description: "Crie vídeos verticais com legendas legíveis.",
        },
        {
          href: sharedUseCaseHrefs.instagram,
          label: "Instagram Reels",
          description: "Produza Reels consistentes sem aparecer.",
        },
        {
          href: sharedUseCaseHrefs.podcast,
          label: "Vídeos de podcast",
          description: "Transforme conversas roteirizadas em episódios visuais.",
        },
      ],
    },
    footer: {
      description:
        "Crie vídeos sem rosto editáveis a partir de uma ideia, roteiro ou URL. Revise antes de baixar, agendar ou publicar.",
      productTitle: "Vídeos sem rosto",
      useCasesTitle: "Casos de uso",
      resourcesTitle: "Recursos",
      terms: "Termos",
      privacy: "Privacidade",
      english: "em inglês",
      productLinks: [
        ["/script-to-faceless-video", "Roteiro para vídeo"],
        ["/url-to-faceless-video", "URL para vídeo"],
        ["/long-form-faceless-video-generator", "Vídeo longo"],
        ["/faceless-video-automation", "Automação de vídeo"],
      ],
      useCaseLinks: [
        [sharedUseCaseHrefs.youtube, "Vídeos para YouTube"],
        [sharedUseCaseHrefs.tiktok, "Vídeos para TikTok"],
        [sharedUseCaseHrefs.instagram, "Instagram Reels"],
        [sharedUseCaseHrefs.podcast, "Vídeos de podcast"],
      ],
      resourceLinks: [
        ["/blog", "Guias (em inglês)"],
        [
          "/resources/faceless-video-script-templates",
          "Modelos de roteiro (em inglês)",
        ],
        [
          "/tools/faceless-video-length-calculator",
          "Calculadora de duração (em inglês)",
        ],
        ["/pricing", "Preços"],
      ],
    },
    marketing: {
      create: "Criar um vídeo sem rosto",
      watchSample: "Ver um exemplo",
      englishSample: "Este exemplo tem áudio ou legendas em inglês.",
      whoFits: "Para quem este fluxo é indicado",
      workflowTitle: "Da entrada ao vídeo aprovado",
      limitsTitle: "Limites práticos para revisar",
      relatedTitle: "Guias e fluxos relacionados",
      faqTitle: "Perguntas frequentes",
      finalTitle: "Crie um vídeo sem rosto revisável",
      finalDescription:
        "Comece com uma ideia, roteiro ou URL. Revise antes de baixar, agendar ou publicar.",
      englishLabel: "em inglês",
      viewPublicExample: "Ver o exemplo público",
    },
  },
  es: {
    metadata: {
      title: "Generador de Vídeos sin Rostro con IA | AriaFlow",
      description:
        "Crea vídeos sin rostro editables desde una idea, guion o URL con imágenes originales, voz de IA, subtítulos, revisión y publicación.",
      keywords: [
        "generador de vídeos sin rostro con IA",
        "crear vídeos sin mostrar el rostro",
        "creador de vídeos sin rostro",
        "automatización de vídeos sin rostro",
      ],
    },
    hero: {
      eyebrow: "Generador de vídeos sin rostro con IA",
      title: "Crea y publica vídeos sin rostro automáticamente",
      description:
        "Empieza con una idea, un guion o una URL pública. Revisa cada detalle y publica a tu ritmo, o automatiza el flujo cuando esté listo.",
      primary: "Empezar gratis",
      secondary: "Ver ejemplos reales",
      previewAlt:
        "Flujo de vídeos sin rostro de AriaFlow con plan de contenido editable y estado de generación",
      caption:
        "Revisa antes de publicar o automatiza el flujo cuando esté listo.",
    },
    workflowTypes: {
      title: "Crea vídeos sin rostro a tu manera.",
      subtitle:
        "Empieza con un tema, tu propio guion o una URL pública. Revisa el borrador antes de generar o publicar.",
      action: "Explorar este flujo",
      items: [
        {
          title: "Tema a vídeo sin rostro",
          description:
            "Crea un plan de contenido y el primer guion desde una idea concreta.",
          output: "Ideal para series nuevas y temas recurrentes",
          href: "/faceless-video-automation",
        },
        {
          title: "Guion a vídeo sin rostro",
          description:
            "Conserva una narración aprobada y conviértela en escenas editables.",
          output: "Ideal para guionistas y contenido revisado",
          href: "/script-to-faceless-video",
        },
        {
          title: "URL a vídeo sin rostro",
          description:
            "Adapta una fuente pública a un borrador hablado y visual.",
          output: "Ideal para artículos y explicaciones documentadas",
          href: "/url-to-faceless-video",
        },
      ],
    },
    samples: {
      title: "Vídeos verticales reales creados con AriaFlow",
      description:
        "Historias, diálogos, reseñas y episodios largos: cada ejemplo proviene de un flujo real de AriaFlow. Compara ritmo, subtítulos y dirección visual antes de crear el tuyo.",
      cta: "Crear un vídeo sin rostro",
      publicExample: "Ver ejemplo público",
      englishDisclosure: "Ejemplo con audio o subtítulos en inglés",
      secondShort: "s",
      minuteShort: "min",
      items: {
        history: {
          label: "Historia",
          title: "La ciudad falsa que recibió las bombas",
          description:
            "Historia vertical sobre señuelos británicos de la Segunda Guerra Mundial, con personajes recurrentes y narración.",
          transcript:
            "El guion explica cómo ciudades iluminadas falsas desviaban bombarderos de Bristol.",
        },
        scary: {
          label: "Historia de terror",
          title: "Una historia de suspense",
          description:
            "Relato vertical con apertura precisa, tensión creciente, subtítulos y dirección visual oscura.",
          transcript:
            "La historia comienza con un detalle inexplicable, aumenta el riesgo escena a escena y termina tras la revelación.",
        },
        longForm: {
          label: "Formato largo",
          title: "Rutina matinal para dejar de sentir pereza",
          description:
            "Pódcast de nueve minutos para aprender inglés con dos voces de IA, subtítulos legibles y un estudio visual coherente.",
          transcript:
            "El episodio enseña vocabulario de rutinas matinales mediante una conversación larga.",
        },
        education: {
          label: "URL a vídeo",
          title: "Ejemplo URL a vídeo de AriaFlow",
          description:
            "La página pública de AriaFlow convertida en un vídeo horizontal de 79 segundos con narración basada en la fuente, subtítulos y escenas de producto.",
          transcript:
            "El guion explica cómo crear vídeos editables desde una idea, un guion o una URL y publicarlos en los canales conectados.",
        },
        url: {
          label: "URL a vídeo",
          title: "Resumen de EnsembleData",
          description:
            "Un sitio condensado en un vídeo de 70 segundos con guion reescrito, escenas de marca, narración y subtítulos.",
          transcript:
            "La fuente se convierte en una explicación de las redes compatibles, la API y sus usos.",
        },
      },
    },
    process: {
      title: "Un flujo completo con revisión antes de publicar",
      description:
        "AriaFlow separa una renderización correcta del permiso para publicar y mantiene visibles los controles de calidad.",
      steps: [
        {
          title: "Entrada",
          description:
            "Empieza con un tema concreto, un guion aprobado o una URL pública.",
        },
        {
          title: "Generación",
          description:
            "Prepara narración, escenas, imágenes originales, subtítulos, música y metadatos.",
        },
        {
          title: "Revisión",
          description:
            "Comprueba guion, voz, imágenes, ritmo, subtítulos y ajustes del canal.",
        },
        {
          title: "Programar o publicar",
          description:
            "Descarga el vídeo aprobado o envíalo a un canal conectado cuando decidas.",
        },
      ],
      controlsTitle: "Mantén el control del borrador",
      controls: [
        "Edita el guion y el plan de escenas generados",
        "Elige voz, estilo visual, relación de aspecto y ritmo",
        "Crea vídeos verticales u horizontales de hasta 10 minutos",
        "Revisa metadatos y cuenta de destino",
      ],
      platformsEyebrow: "Publicación conectada",
      platformsTitle: "Publica donde mira tu audiencia",
      platformsDescription:
        "Conecta cuentas compatibles, revisa el destino y los metadatos y programa o publica el vídeo aprobado.",
      platformsSupportedLabel: "Compatible",
      platformsDisclaimer:
        "La disponibilidad depende de la autorización de la cuenta y de las reglas vigentes de las API.",
      directoryTitle: "Flujos por entrada y plataforma",
      directoryDescription:
        "Elige la página más cercana al vídeo que necesitas. Cada flujo incluye límites, un ejemplo y orientación relacionada.",
      directoryLabel: "Flujos de vídeos sin rostro",
    },
    pricing: {
      title: "Precios adaptados a tu ritmo de producción",
      subtitle:
        "Los créditos cubren guion, imágenes, voz y composición. Consulta una estimación antes de elegir.",
      view: "Ver precios y costes por unidad",
      credits: "créditos incluidos",
      estimate: "Vídeo final estimado",
      disclaimer:
        "La estimación usa unos 500–750 créditos por minuto final. Modelo, voz, ritmo visual, reintentos y duración cambian el consumo real.",
      perYear: "al año",
      perMonth: "al mes",
      minutes: "min",
      planDescriptions: {
        basic: "Para probar el primer flujo y producir borradores consistentes.",
        standard:
          "Para creadores que producen varios vídeos revisados con regularidad.",
        pro: "Para equipos que necesitan más volumen en varios flujos.",
      },
    },
    faq: {
      title: "Preguntas frecuentes",
      description:
        "Conoce las entradas, duración, publicación y derechos antes de crear tu primer vídeo.",
      items: [
        {
          question: "¿Qué es un generador de vídeos sin rostro con IA?",
          answer:
            "Es un flujo que crea vídeos sin que tengas que aparecer en cámara. AriaFlow combina guion, voz de IA, imágenes, subtítulos, música y controles de revisión.",
        },
        {
          question: "¿Puedo usar mi propio guion?",
          answer:
            "Sí. Pega el guion, revisa la duración y las escenas y elige voz, estilo visual, subtítulos, formato y publicación.",
        },
        {
          question: "¿AriaFlow puede convertir una URL en vídeo?",
          answer:
            "AriaFlow procesa páginas públicas compatibles y prepara una adaptación hablada editable. Las páginas privadas o bloqueadas pueden requerir que pegues el texto.",
        },
        {
          question: "¿Cuánto puede durar un vídeo?",
          answer:
            "El flujo actual admite vídeos de hasta 10 minutos. Los créditos y el tiempo dependen de la duración, modelo visual, voz y composición.",
        },
        {
          question: "¿AriaFlow publica automáticamente?",
          answer:
            "Puedes conectar cuentas compatibles de YouTube, TikTok, Instagram y X y revisar el vídeo y el destino antes de programar o publicar.",
        },
        {
          question: "¿El vídeo generado es mío?",
          answer:
            "El archivo final es tuyo, sujeto a tus derechos sobre la fuente y a los términos de modelos, medios, música y voces utilizados.",
        },
      ],
    },
    cta: {
      title: "Crea tu primer vídeo sin rostro revisable",
      description:
        "Empieza con una idea, un guion o una URL y revisa el borrador antes de descargar, programar o publicar.",
      button: "Crear un vídeo sin rostro",
    },
    navigation: {
      facelessVideo: "Vídeo sin rostro",
      useCases: "Casos de uso",
      resources: "Recursos",
      pricing: "Precios",
      dashboard: "Panel",
      signIn: "Iniciar sesión",
      startFree: "Empezar gratis",
      menu: "Menú",
      close: "Cerrar",
      useCaseLinks: [
        {
          href: sharedUseCaseHrefs.youtube,
          label: "Vídeos para YouTube",
          description: "Crea Shorts y vídeos largos para tu canal.",
        },
        {
          href: sharedUseCaseHrefs.tiktok,
          label: "Vídeos para TikTok",
          description: "Crea vídeos verticales con subtítulos legibles.",
        },
        {
          href: sharedUseCaseHrefs.instagram,
          label: "Instagram Reels",
          description: "Produce Reels coherentes sin mostrar el rostro.",
        },
        {
          href: sharedUseCaseHrefs.podcast,
          label: "Vídeos de pódcast",
          description: "Convierte conversaciones escritas en episodios visuales.",
        },
      ],
    },
    footer: {
      description:
        "Crea vídeos sin rostro editables desde una idea, guion o URL. Revisa antes de descargar, programar o publicar.",
      productTitle: "Vídeo sin rostro",
      useCasesTitle: "Casos de uso",
      resourcesTitle: "Recursos",
      terms: "Términos",
      privacy: "Privacidad",
      english: "en inglés",
      productLinks: [
        ["/script-to-faceless-video", "Guion a vídeo"],
        ["/url-to-faceless-video", "URL a vídeo"],
        ["/long-form-faceless-video-generator", "Vídeo largo"],
        ["/faceless-video-automation", "Automatización de vídeo"],
      ],
      useCaseLinks: [
        [sharedUseCaseHrefs.youtube, "Vídeos para YouTube"],
        [sharedUseCaseHrefs.tiktok, "Vídeos para TikTok"],
        [sharedUseCaseHrefs.instagram, "Instagram Reels"],
        [sharedUseCaseHrefs.podcast, "Vídeos de pódcast"],
      ],
      resourceLinks: [
        ["/blog", "Guías (en inglés)"],
        [
          "/resources/faceless-video-script-templates",
          "Plantillas de guion (en inglés)",
        ],
        [
          "/tools/faceless-video-length-calculator",
          "Calculadora de duración (en inglés)",
        ],
        ["/pricing", "Precios"],
      ],
    },
    marketing: {
      create: "Crear un vídeo sin rostro",
      watchSample: "Ver un ejemplo",
      englishSample: "Este ejemplo tiene audio o subtítulos en inglés.",
      whoFits: "Para quién es este flujo",
      workflowTitle: "De la entrada al vídeo aprobado",
      limitsTitle: "Límites prácticos que debes revisar",
      relatedTitle: "Guías y flujos relacionados",
      faqTitle: "Preguntas frecuentes",
      finalTitle: "Crea un vídeo sin rostro revisable",
      finalDescription:
        "Empieza con una idea, un guion o una URL. Revisa antes de descargar, programar o publicar.",
      englishLabel: "en inglés",
      viewPublicExample: "Ver el ejemplo público",
    },
  },
  de: {
    metadata: {
      title: "KI-Videogenerator für gesichtslose Videos | AriaFlow",
      description:
        "Erstelle bearbeitbare gesichtslose Videos aus Idee, Skript oder URL – mit Originalbildern, KI-Stimme, Untertiteln, Prüfung und Veröffentlichung.",
      keywords: [
        "KI-Videogenerator für gesichtslose Videos",
        "gesichtslose KI-Videos erstellen",
        "Videos ohne Gesicht erstellen",
        "Faceless Videos erstellen",
      ],
    },
    hero: {
      eyebrow: "KI-Videogenerator für gesichtslose Videos",
      title: "Erstelle und veröffentliche gesichtslose Videos automatisch",
      description:
        "Starte mit einer Idee, einem Skript oder einer öffentlichen URL. Prüfe jedes Detail und veröffentliche nach Plan oder automatisiere den fertigen Workflow.",
      primary: "Kostenlos starten",
      secondary: "Echte Beispiele ansehen",
      previewAlt:
        "AriaFlow-Workflow für gesichtslose Videos mit bearbeitbarem Inhaltsplan und Generierungsstatus",
      caption:
        "Vor dem Veröffentlichen prüfen oder den fertigen Workflow automatisieren.",
    },
    workflowTypes: {
      title: "Erstelle gesichtslose Videos auf deine Weise.",
      subtitle:
        "Beginne mit einem Thema, deinem Skript oder einer öffentlichen URL. Prüfe den Entwurf vor Generierung oder Veröffentlichung.",
      action: "Workflow ansehen",
      items: [
        {
          title: "Thema zu gesichtslosem Video",
          description:
            "Erstelle aus einer klaren Idee einen Inhaltsplan und das erste Skript.",
          output: "Für neue Serien und wiederkehrende Themen",
          href: "/faceless-video-automation",
        },
        {
          title: "Skript zu gesichtslosem Video",
          description:
            "Übernimm einen geprüften Sprechertext und verwandle ihn in bearbeitbare Szenen.",
          output: "Für Autorinnen, Autoren und geprüfte Inhalte",
          href: "/script-to-faceless-video",
        },
        {
          title: "URL zu gesichtslosem Video",
          description:
            "Bearbeite eine öffentliche Quelle zu einem gesprochenen visuellen Entwurf.",
          output: "Für Artikel und quellenbasierte Erklärvideos",
          href: "/url-to-faceless-video",
        },
      ],
    },
    samples: {
      title: "Echte 9:16-Videos, erstellt mit AriaFlow",
      description:
        "Geschichten, Dialoge, Buchbesprechungen und längere Folgen: Jedes Beispiel stammt aus einem echten AriaFlow-Workflow. Vergleiche Tempo, Untertitel und Bildsprache, bevor du dein eigenes erstellst.",
      cta: "Gesichtsloses Video erstellen",
      publicExample: "Öffentliches Beispiel ansehen",
      englishDisclosure: "Beispiel mit englischem Ton oder Untertiteln",
      secondShort: "Sek.",
      minuteShort: "Min.",
      items: {
        history: {
          label: "Geschichte",
          title: "Die Scheinstadt, die Bomben abfing",
          description:
            "Vertikale Geschichte über britische Ablenkungsanlagen im Zweiten Weltkrieg mit wiederkehrenden Figuren und Sprechertext.",
          transcript:
            "Das Skript erklärt, wie beleuchtete Scheinstädte Bomber von Bristol weglockten.",
        },
        scary: {
          label: "Gruselgeschichte",
          title: "Eine spannende Geschichte",
          description:
            "Vertikale Erzählung mit präzisem Einstieg, steigender Spannung, Untertiteln und dunkler Bildsprache.",
          transcript:
            "Die Geschichte beginnt mit einem unerklärlichen Detail, steigert die Gefahr und endet nach der Auflösung.",
        },
        longForm: {
          label: "Langformat",
          title: "Morgenroutine gegen Antriebslosigkeit",
          description:
            "Neunminütiger Englischlern-Podcast mit zwei KI-Stimmen, lesbaren Untertiteln und konsistentem Studio-Look.",
          transcript:
            "Die Folge vermittelt Wortschatz zur Morgenroutine in einem längeren Gespräch.",
        },
        education: {
          label: "URL zu Video",
          title: "AriaFlow URL-zu-Video-Beispiel",
          description:
            "Die öffentliche AriaFlow-Startseite als 79-sekündiges Breitbildvideo mit quellenbasiertem Sprechertext, Untertiteln und Produktszenen.",
          transcript:
            "Das Skript zeigt, wie bearbeitbare Videos aus einer Idee, einem Skript oder einer URL entstehen und auf verbundenen Kanälen veröffentlicht werden.",
        },
        url: {
          label: "URL zu Video",
          title: "EnsembleData im Überblick",
          description:
            "Eine Website als 70-Sekunden-Video mit neu geschriebenem Skript, Markenszenen, Sprechertext und Untertiteln.",
          transcript:
            "Die Quelle wird zu einer Erklärung unterstützter Netzwerke, API-Zugriff und Anwendungsfälle.",
        },
      },
    },
    process: {
      title: "Vollständiger Workflow mit Prüfung vor Veröffentlichung",
      description:
        "AriaFlow trennt eine erfolgreiche Ausgabe von der Freigabe zur Veröffentlichung, damit Qualitätsprüfungen sichtbar bleiben.",
      steps: [
        {
          title: "Eingabe",
          description:
            "Beginne mit einem klaren Thema, geprüften Skript oder einer öffentlichen URL.",
        },
        {
          title: "Erstellen",
          description:
            "Bereite Sprechertext, Szenen, Originalbilder, Untertitel, Musik und Metadaten vor.",
        },
        {
          title: "Prüfen",
          description:
            "Kontrolliere Skript, Stimme, Bilder, Timing, Untertitel und Kanaleinstellungen.",
        },
        {
          title: "Planen oder veröffentlichen",
          description:
            "Lade das freigegebene Video herunter oder sende es geplant an einen verbundenen Kanal.",
        },
      ],
      controlsTitle: "Behalte die Kontrolle über den Entwurf",
      controls: [
        "Bearbeite das erzeugte Skript und den Szenenplan",
        "Wähle Stimme, Bildstil, Seitenverhältnis und Tempo",
        "Erstelle Hoch- oder Querformatvideos bis zu 10 Minuten",
        "Prüfe Metadaten und Zielkonto",
      ],
      platformsEyebrow: "Verbundene Veröffentlichung",
      platformsTitle: "Veröffentliche dort, wo dein Publikum zusieht",
      platformsDescription:
        "Verbinde unterstützte Konten, prüfe Ziel und Metadaten und plane oder veröffentliche das freigegebene Video.",
      platformsSupportedLabel: "Unterstützt",
      platformsDisclaimer:
        "Die Verfügbarkeit hängt von der Kontoautorisierung und den aktuellen API-Regeln der Plattform ab.",
      directoryTitle: "Workflows nach Eingabe und Plattform",
      directoryDescription:
        "Wähle die Seite, die deinem geplanten Video am nächsten kommt. Jeder Workflow enthält Grenzen, Beispiel und Hilfen.",
      directoryLabel: "Workflows für gesichtslose Videos",
    },
    pricing: {
      title: "Preise passend zu deinem Produktionstempo",
      subtitle:
        "Credits decken Skript, Bilder, Stimme und Komposition ab. Prüfe vor der Wahl eine Planungsbandbreite.",
      view: "Aktuelle Preise und Einzelkosten ansehen",
      credits: "Credits enthalten",
      estimate: "Geschätzte fertige Videolänge",
      disclaimer:
        "Die Planung rechnet mit etwa 500–750 Credits pro fertiger Minute. Modell, Stimme, Bildtempo, Wiederholungen und Länge verändern den Verbrauch.",
      perYear: "pro Jahr",
      perMonth: "pro Monat",
      minutes: "Min.",
      planDescriptions: {
        basic:
          "Zum Testen des ersten Workflows und für eine konsistente Reihe von Entwürfen.",
        standard:
          "Für Kreative, die regelmäßig mehrere geprüfte Videos produzieren.",
        pro: "Für Teams mit höherem Volumen in mehreren Workflows.",
      },
    },
    faq: {
      title: "Häufig gestellte Fragen",
      description:
        "Informiere dich vor dem ersten Video über Eingaben, Länge, Veröffentlichung und Inhaltsrechte.",
      items: [
        {
          question: "Was ist ein KI-Videogenerator für gesichtslose Videos?",
          answer:
            "Er erstellt Videos, ohne dass du vor die Kamera musst. AriaFlow kombiniert Skript, KI-Stimme, Bilder, Untertitel, Musik und Prüfschritte.",
        },
        {
          question: "Kann ich mein eigenes Skript verwenden?",
          answer:
            "Ja. Füge das Skript ein, prüfe Länge und Szenenplan und wähle Stimme, Bildstil, Untertitel, Format und Veröffentlichung.",
        },
        {
          question: "Kann AriaFlow eine URL in ein Video umwandeln?",
          answer:
            "AriaFlow verarbeitet unterstützte öffentliche Seiten und erstellt eine bearbeitbare gesprochene Fassung. Private oder blockierte Seiten erfordern eingefügten Text.",
        },
        {
          question: "Wie lang darf ein Video sein?",
          answer:
            "Der aktuelle Workflow unterstützt bis zu 10 Minuten. Credit-Verbrauch und Erstellungszeit hängen von Länge, Bildmodell, Stimme und Komposition ab.",
        },
        {
          question: "Veröffentlicht AriaFlow automatisch?",
          answer:
            "Du kannst unterstützte Konten von YouTube, TikTok, Instagram und X verbinden und Video sowie Ziel vor Planung oder Veröffentlichung prüfen.",
        },
        {
          question: "Gehört mir das erstellte Video?",
          answer:
            "Die fertige Datei gehört dir, vorbehaltlich deiner Rechte an Quellen und der Bedingungen verwendeter Modelle, Medien, Musik und Stimmen.",
        },
      ],
    },
    cta: {
      title: "Erstelle dein erstes prüfbares Video ohne Gesicht",
      description:
        "Beginne mit Idee, Skript oder URL und prüfe den Entwurf vor Download, Planung oder Veröffentlichung.",
      button: "Gesichtsloses Video erstellen",
    },
    navigation: {
      facelessVideo: "Faceless Video",
      useCases: "Anwendungsfälle",
      resources: "Ressourcen",
      pricing: "Preise",
      dashboard: "Dashboard",
      signIn: "Anmelden",
      startFree: "Kostenlos starten",
      menu: "Menü",
      close: "Schließen",
      useCaseLinks: [
        {
          href: sharedUseCaseHrefs.youtube,
          label: "YouTube-Videos",
          description: "Erstelle Shorts und lange Kanalvideos.",
        },
        {
          href: sharedUseCaseHrefs.tiktok,
          label: "TikTok-Videos",
          description: "Erstelle vertikale Videos mit lesbaren Untertiteln.",
        },
        {
          href: sharedUseCaseHrefs.instagram,
          label: "Instagram Reels",
          description: "Produziere konsistente Reels ohne Auftritt.",
        },
        {
          href: sharedUseCaseHrefs.podcast,
          label: "Podcast-Videos",
          description:
            "Verwandle geschriebene Gespräche in visuelle Folgen.",
        },
      ],
    },
    footer: {
      description:
        "Erstelle bearbeitbare gesichtslose Videos aus Idee, Skript oder URL. Prüfe vor Download, Planung oder Veröffentlichung.",
      productTitle: "Faceless Video",
      useCasesTitle: "Anwendungsfälle",
      resourcesTitle: "Ressourcen",
      terms: "Nutzungsbedingungen",
      privacy: "Datenschutz",
      english: "auf Englisch",
      productLinks: [
        ["/script-to-faceless-video", "Skript zu Video"],
        ["/url-to-faceless-video", "URL zu Video"],
        ["/long-form-faceless-video-generator", "Langformat-Video"],
        ["/faceless-video-automation", "Video-Automatisierung"],
      ],
      useCaseLinks: [
        [sharedUseCaseHrefs.youtube, "YouTube-Videos"],
        [sharedUseCaseHrefs.tiktok, "TikTok-Videos"],
        [sharedUseCaseHrefs.instagram, "Instagram Reels"],
        [sharedUseCaseHrefs.podcast, "Podcast-Videos"],
      ],
      resourceLinks: [
        ["/blog", "Ratgeber (auf Englisch)"],
        [
          "/resources/faceless-video-script-templates",
          "Skriptvorlagen (auf Englisch)",
        ],
        [
          "/tools/faceless-video-length-calculator",
          "Längenrechner (auf Englisch)",
        ],
        ["/pricing", "Preise"],
      ],
    },
    marketing: {
      create: "Gesichtsloses Video erstellen",
      watchSample: "Beispiel ansehen",
      englishSample:
        "Dieses Beispiel enthält englischen Ton oder Untertitel.",
      whoFits: "Für wen dieser Workflow geeignet ist",
      workflowTitle: "Von der Eingabe zum freigegebenen Video",
      limitsTitle: "Praktische Grenzen zur Prüfung",
      relatedTitle: "Verwandte Ratgeber und Workflows",
      faqTitle: "Häufig gestellte Fragen",
      finalTitle: "Erstelle ein prüfbares Video ohne Gesicht",
      finalDescription:
        "Beginne mit Idee, Skript oder URL. Prüfe vor Download, Planung oder Veröffentlichung.",
      englishLabel: "auf Englisch",
      viewPublicExample: "Öffentliches Beispiel ansehen",
    },
  },
  fr: {
    metadata: {
      title: "Générateur de Vidéos sans Visage avec IA | AriaFlow",
      description:
        "Créez des vidéos sans visage modifiables depuis une idée, un script ou une URL avec visuels originaux, voix IA, sous-titres et validation.",
      keywords: [
        "générateur de vidéos sans visage avec IA",
        "créer une vidéo sans visage",
        "créateur de vidéos sans visage",
        "automatisation de vidéos sans visage",
      ],
    },
    hero: {
      eyebrow: "Générateur de vidéos sans visage avec IA",
      title: "Créez et publiez automatiquement des vidéos sans visage",
      description:
        "Partez d’une idée, d’un script ou d’une URL publique. Vérifiez chaque détail, puis publiez à votre rythme ou automatisez le workflow lorsqu’il est prêt.",
      primary: "Commencer gratuitement",
      secondary: "Voir des exemples réels",
      previewAlt:
        "Workflow AriaFlow pour vidéos sans visage avec plan de contenu modifiable et état de génération",
      caption:
        "Validez avant publication ou automatisez le workflow lorsqu’il est prêt.",
    },
    workflowTypes: {
      title: "Créez des vidéos sans visage à votre façon.",
      subtitle:
        "Partez d'un sujet, de votre script ou d'une URL publique. Validez le brouillon avant de générer ou publier.",
      action: "Découvrir ce workflow",
      items: [
        {
          title: "Sujet vers vidéo sans visage",
          description:
            "Créez un plan éditorial et un premier script à partir d'une idée précise.",
          output: "Pour les nouvelles séries et sujets récurrents",
          href: "/faceless-video-automation",
        },
        {
          title: "Script vers vidéo sans visage",
          description:
            "Conservez une narration validée et transformez-la en scènes modifiables.",
          output: "Pour les auteurs et contenus déjà relus",
          href: "/script-to-faceless-video",
        },
        {
          title: "URL vers vidéo sans visage",
          description:
            "Adaptez une source publique en brouillon narré et visuel.",
          output: "Pour les articles et vidéos explicatives sourcées",
          href: "/url-to-faceless-video",
        },
      ],
    },
    samples: {
      title: "De vraies vidéos verticales créées avec AriaFlow",
      description:
        "Histoires, dialogues, critiques de livres et formats longs : chaque exemple provient d’un véritable workflow AriaFlow. Comparez le rythme, les sous-titres et la direction visuelle avant de créer le vôtre.",
      cta: "Créer une vidéo sans visage",
      publicExample: "Voir l'exemple public",
      englishDisclosure: "Exemple avec audio ou sous-titres en anglais",
      secondShort: "s",
      minuteShort: "min",
      items: {
        history: {
          label: "Histoire",
          title: "La fausse ville qui a attiré les bombes",
          description:
            "Récit vertical sur les leurres britanniques de la Seconde Guerre mondiale, avec personnages récurrents et narration.",
          transcript:
            "Le script explique comment des fausses villes éclairées détournaient les bombardiers de Bristol.",
        },
        scary: {
          label: "Histoire effrayante",
          title: "Une histoire à suspense",
          description:
            "Récit vertical avec ouverture précise, tension croissante, sous-titres et direction visuelle sombre.",
          transcript:
            "L'histoire commence par un détail inexpliqué, augmente les enjeux et se termine après la révélation.",
        },
        longForm: {
          label: "Format long",
          title: "Routine matinale pour ne plus se sentir paresseux",
          description:
            "Podcast de neuf minutes pour apprendre l'anglais avec deux voix IA, des sous-titres lisibles et un studio cohérent.",
          transcript:
            "L'épisode enseigne le vocabulaire de la routine matinale à travers une conversation longue.",
        },
        education: {
          label: "URL vers vidéo",
          title: "Exemple URL vers vidéo AriaFlow",
          description:
            "La page publique d’AriaFlow transformée en vidéo horizontale de 79 secondes avec narration issue de la source, sous-titres et scènes produit.",
          transcript:
            "Le script explique comment créer des vidéos modifiables à partir d’une idée, d’un script ou d’une URL, puis les publier sur les canaux connectés.",
        },
        url: {
          label: "URL vers vidéo",
          title: "Présentation d'EnsembleData",
          description:
            "Un site condensé en vidéo de 70 secondes avec script réécrit, scènes de marque, narration et sous-titres.",
          transcript:
            "La source devient une présentation des réseaux pris en charge, de l'API et de ses usages.",
        },
      },
    },
    process: {
      title: "Un workflow complet avec validation avant publication",
      description:
        "AriaFlow sépare la réussite du rendu de l'autorisation de publier afin de garder les contrôles qualité visibles.",
      steps: [
        {
          title: "Entrée",
          description:
            "Partez d'un sujet précis, d'un script validé ou d'une URL publique.",
        },
        {
          title: "Génération",
          description:
            "Préparez narration, scènes, visuels originaux, sous-titres, musique et métadonnées.",
        },
        {
          title: "Validation",
          description:
            "Vérifiez script, voix, visuels, rythme, sous-titres et réglages du canal.",
        },
        {
          title: "Planifier ou publier",
          description:
            "Téléchargez la vidéo validée ou envoyez-la vers un canal connecté au moment choisi.",
        },
      ],
      controlsTitle: "Gardez le contrôle du brouillon",
      controls: [
        "Modifiez le script et le plan de scènes générés",
        "Choisissez voix, style visuel, format et rythme",
        "Créez des vidéos verticales ou horizontales jusqu'à 10 minutes",
        "Validez les métadonnées et le compte de destination",
      ],
      platformsEyebrow: "Publication connectée",
      platformsTitle: "Publiez là où votre audience regarde",
      platformsDescription:
        "Connectez les comptes compatibles, vérifiez destination et métadonnées, puis planifiez ou publiez la vidéo validée.",
      platformsSupportedLabel: "Compatible",
      platformsDisclaimer:
        "La disponibilité dépend de l'autorisation du compte et des règles API actuelles de chaque plateforme.",
      directoryTitle: "Workflows par entrée et plateforme",
      directoryDescription:
        "Choisissez la page la plus proche de votre besoin. Chaque workflow présente ses limites, un exemple et des conseils.",
      directoryLabel: "Workflows de vidéos sans visage",
    },
    pricing: {
      title: "Des tarifs adaptés à votre rythme de production",
      subtitle:
        "Les crédits couvrent script, visuels, voix et composition. Consultez une estimation avant de choisir.",
      view: "Voir les tarifs et coûts unitaires",
      credits: "crédits inclus",
      estimate: "Durée finale estimée",
      disclaimer:
        "L'estimation utilise environ 500–750 crédits par minute finale. Modèle, voix, rythme visuel, nouveaux essais et durée modifient la consommation.",
      perYear: "par an",
      perMonth: "par mois",
      minutes: "min",
      planDescriptions: {
        basic:
          "Pour tester un premier workflow et produire des brouillons cohérents.",
        standard:
          "Pour les créateurs qui produisent régulièrement plusieurs vidéos validées.",
        pro: "Pour les équipes qui ont besoin de plus de volume sur plusieurs workflows.",
      },
    },
    faq: {
      title: "Questions fréquentes",
      description:
        "Comprenez les entrées, la durée, la publication et les droits avant votre première vidéo.",
      items: [
        {
          question:
            "Qu'est-ce qu'un générateur de vidéos sans visage avec IA ?",
          answer:
            "C'est un workflow qui crée une vidéo sans vous demander d'apparaître à l'écran. AriaFlow combine script, voix IA, visuels, sous-titres, musique et validation.",
        },
        {
          question: "Puis-je utiliser mon propre script ?",
          answer:
            "Oui. Collez le script, vérifiez durée et scènes, puis choisissez voix, style visuel, sous-titres, format et publication.",
        },
        {
          question: "AriaFlow peut-il transformer une URL en vidéo ?",
          answer:
            "AriaFlow traite les pages publiques compatibles et prépare une adaptation parlée modifiable. Les pages privées ou bloquées peuvent nécessiter de coller le texte.",
        },
        {
          question: "Quelle peut être la durée d'une vidéo ?",
          answer:
            "Le workflow actuel prend en charge jusqu'à 10 minutes. Crédits et temps de génération varient selon durée, modèle visuel, voix et composition.",
        },
        {
          question: "AriaFlow publie-t-il automatiquement ?",
          answer:
            "Vous pouvez connecter des comptes YouTube, TikTok, Instagram et X compatibles, puis vérifier vidéo et destination avant planification ou publication.",
        },
        {
          question: "La vidéo générée m'appartient-elle ?",
          answer:
            "Le fichier final vous appartient, sous réserve de vos droits sur la source et des conditions des modèles, médias, musiques et voix utilisés.",
        },
      ],
    },
    cta: {
      title: "Créez votre première vidéo sans visage à valider",
      description:
        "Partez d'une idée, d'un script ou d'une URL et vérifiez le brouillon avant téléchargement, planification ou publication.",
      button: "Créer une vidéo sans visage",
    },
    navigation: {
      facelessVideo: "Vidéo sans visage",
      useCases: "Cas d'usage",
      resources: "Ressources",
      pricing: "Tarifs",
      dashboard: "Tableau de bord",
      signIn: "Se connecter",
      startFree: "Commencer gratuitement",
      menu: "Menu",
      close: "Fermer",
      useCaseLinks: [
        {
          href: sharedUseCaseHrefs.youtube,
          label: "Vidéos YouTube",
          description: "Créez des Shorts et des vidéos longues.",
        },
        {
          href: sharedUseCaseHrefs.tiktok,
          label: "Vidéos TikTok",
          description:
            "Créez des vidéos verticales avec des sous-titres lisibles.",
        },
        {
          href: sharedUseCaseHrefs.instagram,
          label: "Instagram Reels",
          description: "Produisez des Reels cohérents sans apparaître.",
        },
        {
          href: sharedUseCaseHrefs.podcast,
          label: "Vidéos de podcast",
          description:
            "Transformez des conversations écrites en épisodes visuels.",
        },
      ],
    },
    footer: {
      description:
        "Créez des vidéos sans visage modifiables depuis une idée, un script ou une URL. Validez avant téléchargement ou publication.",
      productTitle: "Vidéo sans visage",
      useCasesTitle: "Cas d'usage",
      resourcesTitle: "Ressources",
      terms: "Conditions",
      privacy: "Confidentialité",
      english: "en anglais",
      productLinks: [
        ["/script-to-faceless-video", "Script vers vidéo"],
        ["/url-to-faceless-video", "URL vers vidéo"],
        ["/long-form-faceless-video-generator", "Vidéo longue"],
        ["/faceless-video-automation", "Automatisation vidéo"],
      ],
      useCaseLinks: [
        [sharedUseCaseHrefs.youtube, "Vidéos YouTube"],
        [sharedUseCaseHrefs.tiktok, "Vidéos TikTok"],
        [sharedUseCaseHrefs.instagram, "Instagram Reels"],
        [sharedUseCaseHrefs.podcast, "Vidéos de podcast"],
      ],
      resourceLinks: [
        ["/blog", "Guides (en anglais)"],
        [
          "/resources/faceless-video-script-templates",
          "Modèles de script (en anglais)",
        ],
        [
          "/tools/faceless-video-length-calculator",
          "Calculateur de durée (en anglais)",
        ],
        ["/pricing", "Tarifs"],
      ],
    },
    marketing: {
      create: "Créer une vidéo sans visage",
      watchSample: "Voir un exemple",
      englishSample:
        "Cet exemple contient de l'audio ou des sous-titres en anglais.",
      whoFits: "À qui s'adresse ce workflow",
      workflowTitle: "De l'entrée à la vidéo validée",
      limitsTitle: "Limites pratiques à vérifier",
      relatedTitle: "Guides et workflows associés",
      faqTitle: "Questions fréquentes",
      finalTitle: "Créez une vidéo sans visage à valider",
      finalDescription:
        "Partez d'une idée, d'un script ou d'une URL. Vérifiez avant téléchargement, planification ou publication.",
      englishLabel: "en anglais",
      viewPublicExample: "Voir l'exemple public",
    },
  },
  ja: {
    metadata: {
      title: "顔出しなしAI動画生成ツール | AriaFlow",
      description:
        "アイデア、台本、URLから編集可能な顔出しなし動画を作成。オリジナル映像、AI音声、字幕、確認、YouTubeなどへの投稿に対応します。",
      keywords: [
        "顔出しなしAI動画生成ツール",
        "顔出し不要のAI動画",
        "顔出しなしYouTube動画",
        "フェイスレス動画生成",
      ],
    },
    hero: {
      eyebrow: "顔出しなしAI動画生成ツール",
      title: "顔出しなし動画を自動で作成・公開",
      description:
        "アイデア、台本、公開URLから開始。すべてを確認して予定どおりに公開するか、準備ができたワークフローを自動化できます。",
      primary: "無料で始める",
      secondary: "実際のサンプルを見る",
      previewAlt:
        "編集可能なコンテンツ計画と生成状況を表示するAriaFlowの顔出しなし動画ワークフロー",
      caption:
        "公開前に確認することも、準備ができたワークフローを自動化することもできます。",
    },
    workflowTypes: {
      title: "自分に合った方法で顔出しなし動画を作成。",
      subtitle:
        "テーマ、完成した台本、公開URLのいずれからでも開始できます。生成や公開の前に下書きを確認できます。",
      action: "ワークフローを見る",
      items: [
        {
          title: "テーマから顔出しなし動画",
          description:
            "一つの明確なアイデアから、コンテンツ計画と最初の台本を作成します。",
          output: "新しいシリーズや継続テーマ向け",
          href: "/faceless-video-automation",
        },
        {
          title: "台本から顔出しなし動画",
          description:
            "確認済みのナレーションを維持しながら、編集可能なシーンに変換します。",
          output: "ライターや確認済みコンテンツ向け",
          href: "/script-to-faceless-video",
        },
        {
          title: "URLから顔出しなし動画",
          description:
            "公開情報を、音声と映像に適した下書きへ再構成します。",
          output: "記事や出典ベースの解説向け",
          href: "/url-to-faceless-video",
        },
      ],
    },
    samples: {
      title: "AriaFlowで作成した実際の9:16縦型動画",
      description:
        "ストーリー、英会話、書籍レビュー、長尺動画まで、すべて実際のAriaFlowワークフローから生まれたサンプルです。テンポ、字幕、ビジュアルの方向性を比べてから、自分の動画を作れます。",
      cta: "顔出しなし動画を作成",
      publicExample: "公開サンプルを見る",
      englishDisclosure: "英語音声または英語字幕のサンプル",
      secondShort: "秒",
      minuteShort: "分",
      items: {
        history: {
          label: "歴史",
          title: "爆撃を引き受けた偽の町",
          description:
            "第二次世界大戦中の英国の囮施設を扱う縦型歴史動画。継続する登場人物とナレーションを使用しています。",
          transcript:
            "照明で作られた偽の町が、爆撃機をブリストルから遠ざけた仕組みを説明します。",
        },
        scary: {
          label: "怖い話",
          title: "サスペンスストーリー",
          description:
            "明確な導入、段階的な緊張、字幕、暗いビジュアル方向で構成した縦型ストーリーです。",
          transcript:
            "説明のつかない出来事から始まり、シーンごとに危険が増し、真相の後に終わります。",
        },
        longForm: {
          label: "長尺動画",
          title: "怠けた気分を変える朝の習慣",
          description:
            "2人のAI音声、読みやすい字幕、一貫したスタジオ映像を使った9分間の英語学習Podcastです。",
          transcript:
            "長い会話を通して、朝の習慣に関する実用的な英語表現を学びます。",
        },
        education: {
          label: "URLから動画",
          title: "AriaFlow公式サイトのURL動画サンプル",
          description:
            "AriaFlowの公開トップページを、出典に基づくナレーション、字幕、製品シーンを含む79秒の横型動画に変換した例です。",
          transcript:
            "アイデア、台本、URLから編集可能な動画を作成し、接続済みチャンネルへ公開する流れを紹介します。",
        },
        url: {
          label: "URLから動画",
          title: "EnsembleData 製品概要",
          description:
            "Webサイトを70秒の製品紹介にまとめ、書き直した台本、ブランドシーン、音声、字幕を加えています。",
          transcript:
            "対応SNS、APIアクセス、主な用途を説明する動画へ変換しています。",
        },
      },
    },
    process: {
      title: "公開前の確認を含む一貫したワークフロー",
      description:
        "AriaFlowでは動画の生成完了と公開許可を分け、品質確認を見える状態に保ちます。",
      steps: [
        {
          title: "入力",
          description:
            "明確なテーマ、確認済み台本、公開URLから開始します。",
        },
        {
          title: "生成",
          description:
            "ナレーション、シーン、オリジナル映像、字幕、音楽、メタデータを準備します。",
        },
        {
          title: "確認",
          description:
            "台本、音声、映像、タイミング、字幕、チャンネル設定を確認します。",
        },
        {
          title: "予約または公開",
          description:
            "承認済み動画をダウンロードするか、接続したチャンネルへ予約投稿します。",
        },
      ],
      controlsTitle: "下書きの編集権を維持",
      controls: [
        "生成された台本とシーン計画を編集",
        "音声、ビジュアルスタイル、縦横比、テンポを選択",
        "縦型または横型で最長10分の動画を作成",
        "公開メタデータと投稿先アカウントを確認",
      ],
      platformsEyebrow: "接続アカウントへの公開",
      platformsTitle: "視聴者がいる場所へ公開",
      platformsDescription:
        "対応アカウントを接続し、投稿先とメタデータを確認してから、承認済み動画を予約または公開します。",
      platformsSupportedLabel: "対応",
      platformsDisclaimer:
        "公開機能はアカウント認証と各プラットフォームの最新API規則により異なります。",
      directoryTitle: "入力方法とプラットフォーム別のワークフロー",
      directoryDescription:
        "作りたい動画に近いページを選べます。各ページに実用上の制限、サンプル、関連ガイドがあります。",
      directoryLabel: "顔出しなし動画ワークフロー",
    },
    pricing: {
      title: "制作ペースに合わせた料金",
      subtitle:
        "クレジットは台本、映像、音声、合成に使われます。プラン選択前に目安を確認できます。",
      view: "最新料金と単価を見る",
      credits: "クレジット込み",
      estimate: "完成動画の目安",
      disclaimer:
        "完成動画1分あたり約500〜750クレジットで試算しています。モデル、音声、映像テンポ、再生成、動画尺により実際の消費量は変わります。",
      perYear: "年額",
      perMonth: "月額",
      minutes: "分",
      planDescriptions: {
        basic:
          "最初のワークフローを試し、安定した下書きを作成したい方向け。",
        standard: "確認済み動画を定期的に複数制作するクリエイター向け。",
        pro: "複数のワークフローでより多く制作するチーム向け。",
      },
    },
    faq: {
      title: "よくある質問",
      description:
        "最初の動画を作る前に、入力方法、長さ、公開、コンテンツの権利を確認できます。",
      items: [
        {
          question: "顔出しなしAI動画生成ツールとは何ですか？",
          answer:
            "カメラに出演せず動画を作るための制作ワークフローです。AriaFlowは台本、AI音声、映像、字幕、音楽、確認機能を組み合わせます。",
        },
        {
          question: "自分の台本を使えますか？",
          answer:
            "はい。完成した台本を貼り付け、長さとシーン計画を確認し、音声、映像スタイル、字幕、縦横比、公開方法を選べます。",
        },
        {
          question: "URLから動画を作れますか？",
          answer:
            "対応する公開ページを処理し、編集可能なナレーション原稿へ再構成できます。非公開ページや取得を制限するページでは本文の貼り付けが必要です。",
        },
        {
          question: "動画は何分まで作れますか？",
          answer:
            "現在のワークフローは最長10分に対応しています。クレジットと生成時間は長さ、映像モデル、音声、合成設定で変わります。",
        },
        {
          question: "自動で公開できますか？",
          answer:
            "対応するYouTube、TikTok、Instagram、Xアカウントを接続し、動画と投稿先を確認してから予約または公開できます。",
        },
        {
          question: "生成した動画の権利は誰にありますか？",
          answer:
            "完成ファイルは利用者に帰属しますが、元資料の権利と、使用したモデル、素材、音楽、音声の利用条件に従う必要があります。",
        },
      ],
    },
    cta: {
      title: "確認してから公開できる最初の顔出しなし動画を作成",
      description:
        "アイデア、台本、URLから始め、ダウンロード、予約、公開の前に下書きを確認できます。",
      button: "顔出しなし動画を作成",
    },
    navigation: {
      facelessVideo: "顔出しなし動画",
      useCases: "活用例",
      resources: "リソース",
      pricing: "料金",
      dashboard: "ダッシュボード",
      signIn: "ログイン",
      startFree: "無料で始める",
      menu: "メニュー",
      close: "閉じる",
      useCaseLinks: [
        {
          href: sharedUseCaseHrefs.youtube,
          label: "YouTube動画",
          description: "Shortsと長尺のチャンネル動画を作成します。",
        },
        {
          href: sharedUseCaseHrefs.tiktok,
          label: "TikTok動画",
          description: "読みやすい字幕付き縦型動画を作成します。",
        },
        {
          href: sharedUseCaseHrefs.instagram,
          label: "Instagramリール",
          description: "顔出しなしで一貫したリールを作成します。",
        },
        {
          href: sharedUseCaseHrefs.podcast,
          label: "Podcast動画",
          description: "台本付き会話を映像エピソードに変換します。",
        },
      ],
    },
    footer: {
      description:
        "アイデア、台本、URLから編集可能な顔出しなし動画を作成。ダウンロードや公開の前に確認できます。",
      productTitle: "顔出しなし動画",
      useCasesTitle: "活用例",
      resourcesTitle: "リソース",
      terms: "利用規約",
      privacy: "プライバシー",
      english: "英語",
      productLinks: [
        ["/script-to-faceless-video", "台本から動画"],
        ["/url-to-faceless-video", "URLから動画"],
        ["/long-form-faceless-video-generator", "長尺動画"],
        ["/faceless-video-automation", "動画自動化"],
      ],
      useCaseLinks: [
        [sharedUseCaseHrefs.youtube, "YouTube動画"],
        [sharedUseCaseHrefs.tiktok, "TikTok動画"],
        [sharedUseCaseHrefs.instagram, "Instagramリール"],
        [sharedUseCaseHrefs.podcast, "Podcast動画"],
      ],
      resourceLinks: [
        ["/blog", "ガイド（英語）"],
        [
          "/resources/faceless-video-script-templates",
          "台本テンプレート（英語）",
        ],
        [
          "/tools/faceless-video-length-calculator",
          "動画尺計算ツール（英語）",
        ],
        ["/pricing", "料金"],
      ],
    },
    marketing: {
      create: "顔出しなし動画を作成",
      watchSample: "サンプルを見る",
      englishSample: "このサンプルの音声または字幕は英語です。",
      whoFits: "このワークフローが適している方",
      workflowTitle: "入力から承認済み動画まで",
      limitsTitle: "確認すべき実用上の制限",
      relatedTitle: "関連ガイドとワークフロー",
      faqTitle: "よくある質問",
      finalTitle: "確認可能な顔出しなし動画を作成",
      finalDescription:
        "アイデア、台本、URLから始め、ダウンロード、予約、公開の前に確認できます。",
      englishLabel: "英語",
      viewPublicExample: "公開サンプルを見る",
    },
  },
};

export const getWave1HomeContent = (locale: string) =>
  wave1HomeContent[locale as Wave1SeoLocale] ||
  chineseHomeContent[locale as keyof typeof chineseHomeContent] ||
  koreanHomeContent[locale as keyof typeof koreanHomeContent];

export const getHomepageContent = (
  locale: string,
): HomepagePageContent | undefined =>
  getWave1HomeContent(locale) ||
  supplementalHomeContent[locale as keyof typeof supplementalHomeContent];
